import type { Contract, Seat } from "@ravalmatic/bridge-engine/core";
import { GameType, type MatchState, type MatchPartnership } from "../../Types";
import { scoreRubberHand } from "./RubberScoring";

export type MatchProgress =
    | { readonly finished: false; readonly nextHand: number }
    | { readonly finished: true; readonly winningPartnership: MatchPartnership | null };

export class MatchManager {

    constructor(public readonly state: MatchState, public readonly gameType: GameType) {}

    public recordHand(
        handNumber: number, dealer: Seat, contract: Contract | null, declarerTricks: number,
        simulatedPoints: Readonly<Record<MatchPartnership, number>> = { northSouth: 0, eastWest: 0 }
    ): void {

        if (this.state.hands.some(hand => hand.handNumber === handNumber)) {
            throw new Error(`Hand ${handNumber} has already been scored`);
        }
        if (this.getProgress().finished) {
            throw new Error("Cannot record a hand after the match has ended");
        }
        const before = {
            northSouth: this.state.northSouth.totalPoints, eastWest: this.state.eastWest.totalPoints
        };
        if (this.gameType === GameType.RUBBER) {
            scoreRubberHand(this.state, contract, declarerTricks);
        } else {
            for (const partnership of ["northSouth", "eastWest"] as const) {
                if (!Number.isFinite(simulatedPoints[partnership]) || simulatedPoints[partnership] < 0) {
                    throw new Error(`Invalid simulated Chicago points for ${partnership}`);
                }
            }
            this.state.northSouth.totalPoints += simulatedPoints.northSouth;
            this.state.eastWest.totalPoints += simulatedPoints.eastWest;
        }
        const declaring = contract?.declarer;
        const declarerWon = contract !== null && declarerTricks >= contract.level + 6;
        const nsWon = declaring !== undefined && ((declaring % 2 === 0) === declarerWon);
        this.state.hands.push({
            handNumber, dealer, contract: contract ? { ...contract } : null, declarerTricks,
            winningPartnership: contract ? (nsWon ? "northSouth" : "eastWest") : null,
            points: {
                northSouth: this.state.northSouth.totalPoints - before.northSouth,
                eastWest: this.state.eastWest.totalPoints - before.eastWest
            }
        });
    }

    public getProgress(): MatchProgress {

        const { northSouth, eastWest, hands } = this.state;
        if (this.gameType === GameType.RUBBER) {
            if (northSouth.gamesWon >= 2 || eastWest.gamesWon >= 2) {
                return {
                    finished: true,
                    winningPartnership: northSouth.gamesWon >= 2 ? "northSouth" : "eastWest"
                };
            }
        } else if (hands.length >= 4) {
            return {
                finished: true,
                winningPartnership: northSouth.totalPoints === eastWest.totalPoints ? null
                    : northSouth.totalPoints > eastWest.totalPoints ? "northSouth" : "eastWest"
            };
        }
        return {
            finished: false,
            nextHand: (hands[hands.length - 1]?.handNumber ?? this.state.roundNumber) + 1
        };
    }
}
